/*
 *******************************************************************************
 *
 *  Filename:   @/hooks/usePolling.js
 *
 *  Synopsis:   Reusable polling hook. Calls a list of API endpoints on a
 *              fixed interval and returns the latest data keyed by name.
 *              Pauses automatically when the browser tab is hidden.
 *
 *  Author:     Norman J. Nolasco [ Archetact Software ]
 *
 *  Created:    Sunday, February 9, 2026
 *
 *  Usage:
 *    const { data } = usePolling([
 *      { key: "providerStatuses", endpoint: "scheduling", action: "getProviderStatuses", params: {}, responseKey: "rows" },
 *      { key: "managerStatuses",  endpoint: "scheduling", action: "getProviderStatuses", params: { role: "Manager" }, responseKey: "rows" },
 *    ], 10000);
 *
 *    data.providerStatuses  // latest rows
 *    data.managerStatuses   // latest rows
 *
 *  Copyright (c) 2026 - Archetact Software - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { fetchApi } from "@/utils/fetchApi";

/**
 * @param {Array} config - Array of { key, endpoint, action, params, responseKey }
 * @param {number} interval - Polling interval in ms (default 10000)
 * @param {boolean} enabled - When false, skip interval setup entirely (default true)
 * @returns {{ data: Object, pollNow: Function }}
 */
const usePolling = (config, interval = 10000, enabled = true) => {
  const [data, setData] = useState({});
  const configRef = useRef(config);
  const intervalRef = useRef(interval);

  // Keep refs current so the interval callback always uses latest values
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    intervalRef.current = interval;
  }, [interval]);

  const poll = useCallback(async () => {
    const items = configRef.current;
    if (!items || items.length === 0) return;

    const results = await Promise.allSettled(
      items.map((cfg) => fetchApi(cfg.endpoint, cfg.action, cfg.params || {})),
    );

    setData((prev) => {
      const next = { ...prev };
      let changed = false;
      items.forEach((cfg, i) => {
        if (results[i].status === "fulfilled") {
          const responseKey = cfg.responseKey || "rows";
          const newValue = results[i].value[responseKey] || [];
          if (JSON.stringify(prev[cfg.key]) !== JSON.stringify(newValue)) {
            next[cfg.key] = newValue;
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    let timer = null;

    const start = () => {
      if (timer) return;
      timer = setInterval(poll, intervalRef.current);
    };

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        poll(); // Immediate refresh when tab regains focus
        start();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    // Start polling (don't fire immediately — let useFetchData handle the initial load)
    start();

    return () => {
      stop();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [poll, enabled]);

  return { data, pollNow: poll };
};

export default usePolling;
